import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App'
import { bootstrap } from './auth/bootstrap'
import { BrandingProvider } from './brand/BrandingProvider'
import { loadCachedBranding } from './brand/branding'
import { loadRuntimeConfig } from './runtimeConfig'
import './styles/index.css'

/*
  Start-up order (ADR 0020): runtime config → check-sso with a timeout → one of four modes.
  The branding record this browser last saw is read before React renders so the sign-in
  screen never flashes the built-in defaults over a server-edited credit line. Nothing here
  redirects; bootstrap() decides and App renders the branch.
*/
async function start() {
  const root = document.getElementById('root')!

  // The app was a PWA until ADR 0020; drop any service worker a browser still has registered
  // (public/sw.js also removes itself when the browser next checks it).
  void navigator.serviceWorker
    ?.getRegistrations()
    .then((all) => all.forEach((r) => void r.unregister()))

  // An emailed invitation link (/invite/<token>) is opened by someone with no account yet:
  // render the set-a-password page without starting Keycloak.
  const invite = /^\/invite\/([A-Za-z0-9_-]+)\/?$/.exec(window.location.pathname)
  if (invite) {
    await loadRuntimeConfig()
    const [{ InviteAccept }, branding] = await Promise.all([
      import('./screens/InviteAccept'),
      loadCachedBranding().catch(() => null),
    ])
    createRoot(root).render(
      <StrictMode>
        <BrandingProvider initial={branding ?? undefined}>
          <InviteAccept token={invite[1]!} />
        </BrandingProvider>
      </StrictMode>,
    )
    return
  }

  let result
  try {
    result = await bootstrap()
  } catch (error) {
    // Deliberately unbranded: nothing has loaded yet. Classes, not style attributes: the
    // CSP has no 'unsafe-inline'.
    root.replaceChildren()
    const box = document.createElement('div')
    box.className = 'content'
    const title = document.createElement('p')
    title.textContent = 'Could not start.'
    const detail = document.createElement('p')
    detail.className = 'text-muted'
    detail.textContent = String(error)
    box.append(title, detail)
    root.append(box)
    return
  }

  const branding = await loadCachedBranding().catch(() => null)

  if (result.mode === 'login') {
    // keycloak.login() has been called; the page is about to leave.
  }

  createRoot(root).render(
    <StrictMode>
      <App result={result} branding={branding} />
    </StrictMode>,
  )
}

void start()
